import Text from '@shared/components/content/text';
import { Icons } from '@shared/components/content/icons';
import { Button } from '@shared/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@shared/components/ui/popover';
import { cn } from '@shared/lib/utils';
import { cva } from 'class-variance-authority';
import { format } from 'date-fns';
import { useMemo, useState } from 'react';
import {
  MonthKey,
  MonthRange,
  monthOrdinal,
  monthRangeToPeriods,
  periodsToMonthRange,
  sameMonth,
} from '../lib/period-tokens';

type Props = {
  selectedPeriods: string[];
  onChange: (periods: string[]) => void;
};

/** Order two months into a {start, end} range, earliest first. */
function orderedRange(a: MonthKey, b: MonthKey): MonthRange {
  return monthOrdinal(a) <= monthOrdinal(b)
    ? { start: a, end: b }
    : { start: b, end: a };
}

const asDate = ({ year, month }: MonthKey): Date =>
  new Date(year, month - 1, 1);

/** Short month label, e.g. "Jan". */
function monthLabel(m: MonthKey): string {
  return format(asDate(m), 'MMM');
}

/** Concise trigger/footer label, e.g. "Jan 2025 – Dec 2025" or "Jan 2025". */
function formatRange(range: MonthRange | null): string {
  if (!range) return 'All time periods';
  return sameMonth(range.start, range.end)
    ? format(asDate(range.start), 'MMM yyyy')
    : `${format(asDate(range.start), 'MMM yyyy')} – ${format(
        asDate(range.end),
        'MMM yyyy'
      )}`;
}

const monthCell = cva(
  'flex h-8 items-center justify-center rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 disabled:cursor-not-allowed',
  {
    variants: {
      state: {
        default:
          'border border-gray-100 bg-gray-00 text-gray-800 hover:border-primary-400 hover:bg-primary-100',
        inRange: 'border border-transparent bg-primary-200 text-primary-800',
        endpoint:
          'border border-primary-600 bg-primary-600 font-semibold text-gray-00 shadow-sm',
        disabled: 'border border-transparent bg-gray-00 text-gray-300',
      },
    },
    defaultVariants: { state: 'default' },
  }
);

const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

/**
 * Group dashboard period picker — a month-range selector. Pick a start month,
 * then an end month; the span between them is highlighted. Selection is held in
 * draft state and only committed on Apply, so partial/preview ranges don't
 * trigger refetches. Future months are disabled.
 *
 * The contract (`selectedPeriods` in / `onChange` out, both period-token
 * arrays) is unchanged from the previous multi-select picker, so the URL state
 * and `usePeriodDateRange` downstream need no changes. The token grammar lives
 * in `../lib/period-tokens`.
 *
 * Phase 2 will source the available years from the backend group account
 * response instead of the trailing-window default below.
 */
export function GroupPeriodPicker({ selectedPeriods, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<MonthRange | null>(null);
  // Whether the next month click starts a fresh range (vs. closing the current).
  const [picking, setPicking] = useState(false);
  const [hover, setHover] = useState<MonthKey | null>(null);

  const today = useMemo<MonthKey>(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1 };
  }, []);

  // Trailing window of years up to and including the current one (oldest first),
  // matching the design's 2023→2026 layout.
  const years = useMemo(() => {
    const span = 4;
    return Array.from({ length: span }, (_, i) => today.year - (span - 1) + i);
  }, [today.year]);

  const committed = useMemo(
    () => periodsToMonthRange(selectedPeriods),
    [selectedPeriods]
  );

  const handleOpenChange = (next: boolean) => {
    if (next) {
      // Re-seed the draft from the committed selection each time we open, so an
      // abandoned (closed-without-apply) edit doesn't linger.
      setDraft(committed);
      setPicking(false);
      setHover(null);
    }
    setOpen(next);
  };

  const commitRange = (range: MonthRange) => {
    setDraft(range);
    setPicking(false);
    onChange(monthRangeToPeriods(range));
    setOpen(false);
  };

  const handleMonthClick = (m: MonthKey) => {
    if (!draft || !picking) {
      // Begin a new range from this month.
      setDraft({ start: m, end: m });
      setPicking(true);
      return;
    }
    // Second click closes the range — order endpoints, commit, close.
    commitRange(orderedRange(draft.start, m));
  };

  // While picking, preview the range out to the hovered month.
  const preview: MonthRange | null = useMemo(() => {
    if (!draft) return null;
    if (picking && hover) return orderedRange(draft.start, hover);
    return draft;
  }, [draft, picking, hover]);

  const cellState = (m: MonthKey) => {
    const o = monthOrdinal(m);
    if (o > monthOrdinal(today)) return 'disabled' as const;
    if (!preview) return 'default' as const;
    if (sameMonth(m, preview.start) || sameMonth(m, preview.end)) {
      return 'endpoint' as const;
    }
    if (o > monthOrdinal(preview.start) && o < monthOrdinal(preview.end)) {
      return 'inRange' as const;
    }
    return 'default' as const;
  };

  const yearInRange = (year: number) =>
    !!preview && year >= preview.start.year && year <= preview.end.year;

  const handleYearClick = (year: number) => {
    // Year > current year would only render disabled months — skip.
    if (year > today.year) return;
    // For past years select the full year; for the current year cap the
    // end at the latest available month so we don't enter a disabled range.
    const endMonth = year === today.year ? today.month : 12;
    commitRange({ start: { year, month: 1 }, end: { year, month: endMonth } });
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="h-10 justify-between gap-2 px-3 font-normal shadow-none"
          icon={<Icons.calendar />}
        >
          {formatRange(committed)}
          <Icons.chevronDown className="ml-1 h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[360px] overflow-hidden rounded-xl p-0 shadow-lg"
      >
        <div className="max-h-[62vh] space-y-4 overflow-y-auto p-4">
          {years.map((year) => (
            <div key={year} className="space-y-2">
              <button
                type="button"
                onClick={() => handleYearClick(year)}
                disabled={year > today.year}
                className="block text-left transition-opacity hover:opacity-60 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:opacity-100"
              >
                <Text
                  variant="subSection"
                  className={cn(
                    'text-lg font-semibold',
                    yearInRange(year) ? 'text-primary-700' : 'text-gray-900'
                  )}
                >
                  {year}
                </Text>
              </button>
              <div className="grid grid-cols-6 gap-1.5">
                {MONTHS.map((month) => {
                  const m = { year, month };
                  const state = cellState(m);
                  return (
                    <button
                      key={month}
                      type="button"
                      disabled={state === 'disabled'}
                      onClick={() => handleMonthClick(m)}
                      onMouseEnter={() => setHover(m)}
                      onMouseLeave={() => setHover(null)}
                      className={cn(monthCell({ state }))}
                      aria-label={format(asDate(m), 'MMMM yyyy')}
                      aria-pressed={state !== 'default'}
                    >
                      {monthLabel(m)}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
